import { BehaviorSubject } from "rxjs";
let ws = null;
export let seriesComponentMapping = {};
let onCloseListener, onErrorListener;
import store from "@/store/index";
import Vue from "vue";

import { DownloadManager } from "dv-download-manager";
export let dm = new DownloadManager("alternate", false);

// Track per-series download progress as loaded/total counts.
const downloadCounts = {};

// Accumulator for in-progress chunked downloads, keyed by db_id (Image PK).
// Each entry: { chunks: Uint8Array[], received: number, totalSize: number|null,
//              seriesId, elementId, imageId, annotations }
const pendingChunks = {};

const getSeriesLabel = seriesId => {
  try {
    const studies = store.state.viewer.data || [];
    for (const study of studies) {
      const s = (study.series || []).find(s => s.id == seriesId);
      if (s) {
        return (s.series_description || "").trim() || null;
      }
    }
  } catch (_) {
    // ignore — store may not have study data yet
  }
  return null;
};

const updateDownloadProgress = seriesId => {
  const entry = downloadCounts[seriesId];
  if (!entry) return;
  const progress = Math.round((100 * entry.loaded) / entry.total);
  store.commit("viewer/setDownloadProgress", {
    seriesId,
    value: progress,
    loaded: entry.loaded,
    total: entry.total
  });
};

import { decode } from "@msgpack/msgpack/dist";
const URL =
  window.location.protocol == "https:"
    ? `wss:/${window.location.host}/ws/series/download/`
    : `ws:/${window.location.host}/ws/series/download/`;

const wsStatus = new BehaviorSubject();
const DEFAULT_DOWNLOAD_WINDOW = 2;
const MAX_IN_FLIGHT_DOWNLOADS = DEFAULT_DOWNLOAD_WINDOW;

const DEFAULT_MAX_PARSE_QUEUE = 10;
const MAX_PARSE_QUEUE = DEFAULT_MAX_PARSE_QUEUE;
let inFlightDownloads = 0;
const parseQueue = [];
let isParsing = false;

const decrementInFlightDownloads = () => {
  inFlightDownloads = Math.max(0, inFlightDownloads - 1);
};

const resetPipelineState = () => {
  inFlightDownloads = 0;
  parseQueue.length = 0;
  isParsing = false;
  // Discard any partially-received chunked downloads so that stale
  // accumulators do not consume memory after a disconnection.
  for (const key of Object.keys(pendingChunks)) {
    delete pendingChunks[key];
  }
};

const toUint8Array = payload => {
  if (!payload) return null;
  if (payload instanceof Uint8Array) return payload;
  if (payload instanceof ArrayBuffer) return new Uint8Array(payload);
  if (ArrayBuffer.isView(payload)) {
    return new Uint8Array(
      payload.buffer,
      payload.byteOffset,
      payload.byteLength
    );
  }
  return null;
};

const supportsParseBytes = () =>
  typeof Vue.prototype.$dicom?.parseBytes === "function" &&
  (typeof Vue.prototype.$dicom?.supportsNativeParseBytes === "function"
    ? Vue.prototype.$dicom.supportsNativeParseBytes()
    : true);
const yieldToMainThread = () => new Promise(resolve => setTimeout(resolve, 0));

export const statusSource = wsStatus;

export const downloadDicomFiles = async (
  seriesId,
  elementId,
  images,
  updateProgress
) => {
  seriesComponentMapping[seriesId] = updateProgress;

  // Initialize download counter for this series
  downloadCounts[seriesId] = { loaded: 0, total: images.length };
  updateDownloadProgress(seriesId);

  // add series to the download manager
  dm.addSeries(seriesId, seriesId, elementId, images);

  // start downloading if the download is not already in progress
  processDownloadQueue();
};

export const stopDownloadingDicomFiles = id => {
  dm.removeSeries(id);
  delete downloadCounts[id];
  store.commit("viewer/setDownloadProgress", { seriesId: id, value: null });
};

// interface with download manager
export function processDownloadQueue() {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  if (parseQueue.length >= MAX_PARSE_QUEUE) {
    console.warn(
      "[series-download] parse queue backlog threshold reached",
      parseQueue.length
    );
    return;
  }

  while (inFlightDownloads < MAX_IN_FLIGHT_DOWNLOADS) {
    // get next slice to download
    const slot = dm
      .getNextSlot(1)
      .slice()
      .pop();

    if (!slot) {
      return;
    }

    // prepare the message
    const wsMessage = {
      data: {
        images: [slot.imageId], // ids of the series' images object from the database
        elementIds: [slot.studyId]
      }
    };

    try {
      // send the message
      send(JSON.stringify(wsMessage));
      inFlightDownloads++;
    } catch (sendError) {
      console.error("Failed to send series download message", sendError);
      return;
    }
  }
}

async function drainParseQueue() {
  if (isParsing) return;
  isParsing = true;

  while (parseQueue.length > 0) {
    const task = parseQueue.shift();

    try {
      const parserUsed =
        task.byteArray && supportsParseBytes() ? "parseBytes" : "parseFile";
      const fallbackFile = task.file
        ? task.file
        : task.byteArray
        ? new File([task.byteArray], task.fileName || "dicom-bytes.dcm", {
            type: "application/dicom"
          })
        : null;
      let parsedResult;
      if (parserUsed === "parseBytes") {
        parsedResult = await Vue.prototype.$dicom.parseBytes(
          task.seriesId,
          task.byteArray,
          task.fileName,
          task.elementId
        );
      } else {
        if (!fallbackFile) {
          throw new Error("Missing DICOM parse input");
        }
        parsedResult = await Vue.prototype.$dicom.parseFile(
          task.seriesId,
          fallbackFile,
          task.elementId
        );
      }
      const { imageIds, imageId } = parsedResult;

      if (typeof seriesComponentMapping[task.seriesId] === "function") {
        seriesComponentMapping[task.seriesId](
          task.seriesId,
          task.elementId,
          imageIds,
          imageId
        );
      }

      if (downloadCounts[task.seriesId]) {
        downloadCounts[task.seriesId].loaded++;
        updateDownloadProgress(task.seriesId);
      }

      const progress = dm.getStatus(task.seriesId);
      if (progress === null) {
        console.warn("Download ends for series", task.seriesId);
        store.commit("viewer/downloadEnded", { seriesId: task.seriesId });
        store.commit("viewer/setDownloadProgress", {
          seriesId: task.seriesId,
          value: null
        });
        delete downloadCounts[task.seriesId];
        const seriesLabel = getSeriesLabel(task.seriesId);
        store.dispatch("raiseSnackbar", {
          text: seriesLabel
            ? `Download complete: ${seriesLabel} — advanced views now available`
            : "Series download complete — advanced views now available",
          color: "primary",
          timeout: 3000
        });
        dm.removeSeries(task.seriesId);
      }
    } catch (error) {
      console.error(error);
      store.dispatch("raiseSnackbar", {
        text: error || "Failed to download series",
        color: "error",
        timeout: -1
      });
    } finally {
      decrementInFlightDownloads();
      processDownloadQueue();
      await yieldToMainThread();
    }
  }

  isParsing = false;
}

export function openSeriesWebsocket(userID, onWsClose, onWsError, router) {
  if (ws) return;
  const onClose = event => {
    const readyState = ws ? ws.readyState : WebSocket.CLOSED;
    resetPipelineState();
    console.warn("WS", event.type, event);
    wsStatus.next(readyState);
    onWsClose(event);
  };

  const onError = event => {
    const readyState = ws ? ws.readyState : WebSocket.CLOSED;
    resetPipelineState();
    console.warn("WS", event.type, event);
    if (readyState == WebSocket.OPEN) {
      onWsError(event);
    }
  };

  ws = new WebSocket(URL + userID + `/`);
  wsStatus.next(ws.readyState);

  ws.onopen = () => {
    console.warn("Connected to WebSocket");
    wsStatus.next(ws.readyState);
    processDownloadQueue();
  };

  onCloseListener = event => onClose(event);
  onErrorListener = event => onError(event);

  ws.onclose = onCloseListener;
  ws.onerror = onErrorListener;

  const onReceiveData = async (message, error) => {
    const isViewerRoute =
      router.history.current.name === "viewer" ||
      router.history.current.name === "share";

    // Even when moving away from viewer during the download, 'progress' is null
    // viewer/downloadEnded already called on Viewer.vue::beforeDestroy()
    if (!isViewerRoute) {
      if (message && message.data instanceof Blob) {
        decrementInFlightDownloads();
      }
      return;
    }

    // ----------------------------------------------------------------
    // Read raw buffer
    // ----------------------------------------------------------------
    if (!(message.data instanceof Blob)) {
      decrementInFlightDownloads();
      processDownloadQueue();
      store.dispatch("raiseSnackbar", {
        text: error || "Failed to download series: no data received",
        color: "error",
        timeout: -1
      });
      return;
    }

    let packedBuffer;
    try {
      packedBuffer = await message.data.arrayBuffer();
    } catch (arrayBufferError) {
      decrementInFlightDownloads();
      processDownloadQueue();
      console.error("Failed to read WS DICOM payload", arrayBufferError);
      store.dispatch("raiseSnackbar", {
        text: "Failed to read downloaded DICOM payload",
        color: "error",
        timeout: -1
      });
      return;
    }

    let msg;
    try {
      msg = decode(packedBuffer);
    } catch (decodeError) {
      decrementInFlightDownloads();
      processDownloadQueue();
      console.error("Failed to decode WS DICOM payload", decodeError);
      store.dispatch("raiseSnackbar", {
        text: "Failed to decode downloaded DICOM payload",
        color: "error",
        timeout: -1
      });
      return;
    }

    // ----------------------------------------------------------------
    // Chunked protocol (type === "chunk")
    // ----------------------------------------------------------------
    if (msg && msg.type === "chunk") {
      const {
        db_id,
        image_id,
        series_id,
        element_id,
        chunk_index,
        is_last,
        data: chunkData,
        annotations,
        total_size
      } = msg;

      // First chunk: initialise accumulator and store annotations.
      if (chunk_index === 0) {
        pendingChunks[db_id] = {
          chunks: [],
          received: 0,
          totalSize: total_size ?? null,
          seriesId: series_id,
          elementId: element_id,
          imageId: image_id,
          annotations: annotations ?? []
        };
        if (annotations && annotations.length > 0) {
          store.commit("viewer/setAnnotations", {
            imageId: db_id,
            annotations
          });
        }
      }

      const pending = pendingChunks[db_id];
      if (!pending) {
        // Orphan chunk (e.g. after a reset): discard silently.
        if (is_last) {
          decrementInFlightDownloads();
          processDownloadQueue();
        }
        return;
      }

      // Accumulate payload bytes.
      if (chunkData && chunkData.length > 0) {
        const chunk = toUint8Array(chunkData);
        pending.chunks.push(chunk);
        pending.received += chunk.length;
      }

      if (!is_last) {
        // Emit byte-level progress so the bar animates while chunks arrive.
        // Maps the in-progress image's byte fraction into the overall
        // series progress (between already-loaded images and the next one).
        if (pending.totalSize) {
          const counts = downloadCounts[pending.seriesId];
          if (counts) {
            const alreadyLoaded = counts.loaded;
            const totalImages = counts.total;
            const imageFraction = pending.received / pending.totalSize;
            const overallValue = Math.round(
              ((alreadyLoaded + imageFraction) / totalImages) * 100
            );
            store.commit("viewer/setDownloadProgress", {
              seriesId: pending.seriesId,
              value: overallValue,
              loaded: alreadyLoaded,
              total: totalImages
            });
          }
        }
        return;
      }

      // ---- Final chunk received: assemble the full DICOM file ----
      delete pendingChunks[db_id];

      const totalLen = pending.chunks.reduce((s, c) => s + c.length, 0);
      const fullArray = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of pending.chunks) {
        fullArray.set(chunk, offset);
        offset += chunk.length;
      }

      parseQueue.push({
        seriesId: pending.seriesId,
        elementId: pending.elementId,
        byteArray: fullArray,
        fileName: pending.imageId
      });

      // decrementInFlightDownloads + processDownloadQueue are called inside
      // drainParseQueue's finally block, so we must not call them here.
      drainParseQueue();
      return;
    }

    // ----------------------------------------------------------------
    // Unknown message format — surface an error.
    // ----------------------------------------------------------------
    decrementInFlightDownloads();
    processDownloadQueue();
    console.error("Received unexpected WS message format", msg);
    store.dispatch("raiseSnackbar", {
      text: "Failed to download series: unexpected payload format",
      color: "error",
      timeout: -1
    });
  };

  setOnMessage(onReceiveData);
  setOnError(onReceiveData);
}

export function setOnMessage(onmessage) {
  ws.onmessage = onmessage;
}
export function setOnError(onerror) {
  ws.onerror = onerror;
}

export const canSendMessage = () => {
  return ws !== null && typeof ws.send === "function";
};

export const send = message => ws.send(message);

export const disconnect = () => {
  if (ws && typeof ws.close === "function") {
    ws.close();
    ws = null;
    resetPipelineState();
  }
};
